import {
  DEFAULT_WEIGHT_SETTINGS,
  isWeightUnit,
  weightEntryId,
  weightSettingsId,
  type LocalDate,
  type WeightEntryLike,
  type WeightSettings,
} from '@tahi/core';
import { weightEntries, weightSettings } from '@tahi/db';
import { and, asc, eq, isNull } from 'drizzle-orm';
import type { LocalDb } from '../localDb';

export const WEIGHT_TABLES = ['weight_entries', 'weight_settings'];

type EntryRow = typeof weightEntries.$inferSelect;

function toEntry(r: EntryRow): WeightEntryLike {
  return {
    id: r.id,
    date: r.date,
    valueG: r.valueG,
    unit: isWeightUnit(r.unit) ? r.unit : 'kg',
    note: r.note ?? null,
    deletedAt: r.deletedAt ?? null,
  };
}

/** Every live weigh-in, oldest first. */
export async function listWeightEntries(db: LocalDb, userId: string): Promise<WeightEntryLike[]> {
  const rows = await db
    .select()
    .from(weightEntries)
    .where(and(eq(weightEntries.userId, userId), isNull(weightEntries.deletedAt)))
    .orderBy(asc(weightEntries.date))
    .all();
  return rows.map(toEntry);
}

/** A day's entry, deleted or not (Undo of a delete reads the deleted one). */
export async function getWeightEntry(
  db: LocalDb,
  userId: string,
  date: LocalDate,
): Promise<WeightEntryLike | null> {
  const r = await db
    .select()
    .from(weightEntries)
    .where(and(eq(weightEntries.userId, userId), eq(weightEntries.id, weightEntryId(userId, date))))
    .get();
  return r ? toEntry(r) : null;
}

/** The settings singleton, or the defaults before the first write. */
export async function getWeightSettings(db: LocalDb, userId: string): Promise<WeightSettings> {
  const r = await db
    .select()
    .from(weightSettings)
    .where(and(eq(weightSettings.userId, userId), eq(weightSettings.id, weightSettingsId(userId))))
    .get();
  if (!r || r.deletedAt) return DEFAULT_WEIGHT_SETTINGS;
  return {
    unit: isWeightUnit(r.unit) ? r.unit : 'kg',
    goalG: r.goalG ?? null,
    goalStartG: r.goalStartG ?? null,
    goalStartDate: r.goalStartDate ?? null,
  };
}
